import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  CircularProgress,
  Button,
  Avatar,
  Stack,
  Divider,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import BugReportIcon from "@mui/icons-material/BugReport";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PersonIcon from "@mui/icons-material/Person";
import CakeIcon from "@mui/icons-material/Cake";
import WorkIcon from "@mui/icons-material/Work";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { dashboardAPI } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/common/Toast";

const STATUS_COLORS = {
  planning: "#4472C4",
  active: "#2E7D32",
  on_hold: "#E65100",
  completed: "#0277BD",
  cancelled: "#C62828",
};
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const LEAVE_COLORS = {
  casual: "#1565C0",
  sick: "#E65100",
  earned: "#2E7D32",
  maternity: "#AD1457",
  paternity: "#6A1B9A",
  unpaid: "#424242",
};

function StatCard({ label, value, icon, gradient, subtitle, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        height: "100%",
        overflow: "hidden",
        position: "relative",
        border: "none",
        background: gradient,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.18s, box-shadow 0.18s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: onClick ? "0 10px 32px rgba(0,0,0,0.22)" : "0 8px 28px rgba(0,0,0,0.18)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          right: -12,
          top: -12,
          width: 70,
          height: 70,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.1)",
        }}
      />
      <CardContent
        sx={{
          position: "relative",
          zIndex: 1,
          p: { xs: 2, md: 2 },
          "&:last-child": { pb: "16px !important" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              flexShrink: 0,
            }}
          >
            {React.cloneElement(icon, { fontSize: "small" })}
          </Box>
          <TrendingUpIcon
            sx={{ color: "rgba(255,255,255,0.4)", fontSize: 15 }}
          />
        </Box>
        <Typography
          sx={{
            fontWeight: 900,
            color: "#fff",
            lineHeight: 1,
            mb: 0.4,
            fontSize: { xs: "2rem", md: "2.2rem" },
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.85)",
            fontWeight: 700,
            fontSize: { xs: "0.65rem", md: "0.68rem" },
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255,255,255,0.6)",
              display: "block",
              mt: 0.2,
              fontSize: "0.6rem",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function EventCard({
  title,
  icon,
  iconColor,
  gradient,
  borderColor,
  items,
  emptyMsg,
}) {
  const today = new Date().getDate();
  const monthName = MONTHS[new Date().getMonth()];
  const year = new Date().getFullYear();
  return (
    <Card
      sx={{
        height: "100%",
        border: `1px solid ${borderColor}30`,
        boxShadow: `0 4px 20px ${borderColor}15`,
      }}
    >
      <Box
        sx={{
          background: gradient,
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: "#fff", lineHeight: 1.2 }}
            >
              {title}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.75)" }}
            >
              {monthName} {year}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 900, color: "#fff" }}>
            {items.length}
          </Typography>
        </Box>
      </Box>
      <CardContent sx={{ p: 2 }}>
        {items.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography sx={{ fontSize: "2rem", mb: 0.5 }}>
              {title.includes("Birthday")
                ? "🎂"
                : title.includes("Work")
                  ? "💼"
                  : "💝"}
            </Typography>
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ fontWeight: 500 }}
            >
              {emptyMsg}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {items.map((item, i) => {
              const isToday = item.day === today;
              const isPast = item.day < today;
              return (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.2,
                    borderRadius: 2,
                    bgcolor: isToday
                      ? iconColor + "12"
                      : isPast
                        ? "transparent"
                        : "background.default",
                    border: isToday
                      ? `1.5px solid ${iconColor}50`
                      : "1.5px solid transparent",
                    opacity: isPast ? 0.45 : 1,
                    transition: "all 0.15s",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: isToday ? iconColor : iconColor + "20",
                      color: isToday ? "#fff" : iconColor,
                      fontWeight: 800,
                      fontSize: "0.9rem",
                      flexShrink: 0,
                    }}
                  >
                    {item.name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        {item.day} {monthName}
                      </Typography>
                      {item.years > 0 && (
                        <Chip
                          label={
                            item.years + (item.years === 1 ? " yr" : " yrs")
                          }
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            bgcolor: iconColor + "18",
                            color: iconColor,
                            px: 0.2,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  {isToday && (
                    <Typography sx={{ fontSize: "1.2rem", flexShrink: 0 }}>
                      🎉
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const isTeamMember = user?.role === "team_member";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = () => {
    setLoading(true);
    setLoadFailed(false);
    dashboardAPI
      .get()
      .then((res) => setData(res.data))
      .catch(() => {
        setLoadFailed(true);
        showToast("Failed to load dashboard", "error");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  if (loadFailed || !data)
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 10 }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Couldn't load the dashboard.
        </Typography>
        <Button variant="outlined" onClick={load}>Retry</Button>
      </Box>
    );

  const { stats, recentProjects, events, upcomingLeaves = [] } = data;
  const today = new Date().getDate();
  // Filter out past dates as safety net (backend should do this too)
  const filterUpcoming = (arr) => (arr || []).filter((i) => i.day >= today);
  const upcomingBirthdays = filterUpcoming(events?.upcomingBirthdays);
  const workAnniversaries = filterUpcoming(events?.workAnniversaries);
  const weddingAnniversaries = filterUpcoming(events?.weddingAnniversaries);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Morning" : hour < 17 ? "Afternoon" : "Evening";
  const greetEmoji = hour < 12 ? "🌤️" : hour < 17 ? "☀️" : "🌙";
  const todayStr = new Date().toISOString().split("T")[0];

  const teamCards = [
    {
      label: "Assigned Projects",
      value: stats.assignedProjects ?? stats.totalProjects,
      icon: <PersonIcon />,
      gradient: "linear-gradient(135deg, #1E3A5F 0%, #2E75B6 100%)",
      subtitle: "Your projects",
      onClick: () => navigate("/projects"),
    },
    {
      label: "Active",
      value: stats.activeProjects,
      icon: <PlayArrowIcon />,
      gradient: "linear-gradient(135deg, #1B5E20 0%, #43A047 100%)",
      onClick: () => navigate("/projects?status=active"),
    },
    {
      label: "Assigned Defects",
      value: stats.openDefects,
      icon: <BugReportIcon />,
      gradient: "linear-gradient(135deg, #B71C1C 0%, #E53935 100%)",
      subtitle: "Open bugs",
    },
    {
      label: "Status This Week",
      value: stats.myStatusCount ?? 0,
      icon: <AssignmentTurnedInIcon />,
      gradient: "linear-gradient(135deg, #4A148C 0%, #8E24AA 100%)",
      subtitle: "Updates submitted",
    },
  ];

  const adminCards = [
    {
      label: "Total Projects",
      value: stats.totalProjects,
      icon: <FolderIcon />,
      gradient: "linear-gradient(135deg, #1E3A5F 0%, #2E75B6 100%)",
      onClick: () => navigate("/projects"),
    },
    {
      label: "Active",
      value: stats.activeProjects,
      icon: <PlayArrowIcon />,
      gradient: "linear-gradient(135deg, #1B5E20 0%, #43A047 100%)",
      onClick: () => navigate("/projects?status=active"),
    },
    {
      label: "On Hold",
      value: stats.onHoldProjects,
      icon: <PauseIcon />,
      gradient: "linear-gradient(135deg, #BF360C 0%, #F4511E 100%)",
      onClick: () => navigate("/projects?status=on_hold"),
    },
    {
      label: "Completed",
      value: stats.completedProjects,
      icon: <CheckCircleIcon />,
      gradient: "linear-gradient(135deg, #01579B 0%, #039BE5 100%)",
      onClick: () => navigate("/projects?status=completed"),
    },
    {
      label: "Overdue",
      value: stats.overdueProjects,
      icon: <WarningIcon />,
      gradient: "linear-gradient(135deg, #B71C1C 0%, #E53935 100%)",
      subtitle: "Need attention",
      onClick: () => navigate("/projects?status=overdue"),
    },
    {
      label: "Open Defects",
      value: stats.openDefects,
      icon: <BugReportIcon />,
      gradient: "linear-gradient(135deg, #4A148C 0%, #8E24AA 100%)",
    },
    {
      label: "Critical Bugs",
      value: stats.criticalDefects,
      icon: <BugReportIcon />,
      gradient: "linear-gradient(135deg, #880E4F 0%, #D81B60 100%)",
      subtitle: "Urgent fix needed",
    },
  ];

  const cards = isTeamMember ? teamCards : adminCards;

  return (
    <Box>
      {/* Hero greeting */}
      <Box
        sx={{
          mb: 4,
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          background:
            "linear-gradient(135deg, #1E3A5F 0%, #2E75B6 60%, #4FC3F7 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: -40,
            top: -40,
            width: 200,
            height: 200,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.05)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            right: 60,
            bottom: -60,
            width: 150,
            height: 150,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.07)",
          }}
        />
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              color: "#fff",
              mb: 0.5,
              fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2.125rem" },
              lineHeight: 1.25,
            }}
          >
            Good {greeting}, {user?.name?.split(" ")[0]} {greetEmoji}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.75)", fontWeight: 500, fontSize: { xs: "0.8rem", sm: "1rem" } }}>
            {new Date().toLocaleDateString("en-IN", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </Typography>
        </Box>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {cards.map((c, i) => (
          <Grid
            item
            xs={6}
            sm={4}
            md={isTeamMember ? 3 : 12 / 7}
            lg={isTeamMember ? 3 : 12 / 7}
            key={i}
          >
            <StatCard {...c} />
          </Grid>
        ))}
      </Grid>

      {/* Upcoming Leaves */}
      {upcomingLeaves.length > 0 && (
        <Card
          sx={{
            mb: 4,
            border: "1px solid #1565C030",
            boxShadow: "0 4px 20px #1565C015",
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)",
              px: 2.5,
              py: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                }}
              >
                <BeachAccessIcon />
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, color: "#fff", lineHeight: 1.2 }}
                >
                  Upcoming Leaves
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.75)" }}
                >
                  Approved leave requests
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 900, color: "#fff" }}
              >
                {upcomingLeaves.length}
              </Typography>
            </Box>
          </Box>
          <CardContent sx={{ p: 2 }}>
            <Grid container spacing={1.5}>
              {upcomingLeaves.map((l, i) => {
                const color = LEAVE_COLORS[l.leave_type] || "#1565C0";
                const isOnLeave =
                  l.from_date <= todayStr && l.to_date >= todayStr;
                return (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: isOnLeave
                          ? color + "10"
                          : "background.default",
                        border: "1px solid",
                        borderColor: isOnLeave ? color + "40" : "divider",
                        transition: "all 0.15s",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: color + "20",
                          color,
                          fontWeight: 800,
                          fontSize: "0.9rem",
                          flexShrink: 0,
                        }}
                      >
                        {l.employee?.name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {l.employee?.name}
                          </Typography>
                          {isOnLeave && (
                            <Chip
                              label="On Leave"
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: "0.58rem",
                                fontWeight: 700,
                                bgcolor: color,
                                color: "#fff",
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {(() => {
                            const fmt = (d) => {
                              const p = d.split("-");
                              const months = [
                                "Jan",
                                "Feb",
                                "Mar",
                                "Apr",
                                "May",
                                "Jun",
                                "Jul",
                                "Aug",
                                "Sep",
                                "Oct",
                                "Nov",
                                "Dec",
                              ];
                              return `${parseInt(p[2])} ${months[parseInt(p[1]) - 1]} ${p[0]}`;
                            };
                            return l.from_date === l.to_date
                              ? fmt(l.from_date)
                              : `${fmt(l.from_date)} - ${fmt(l.to_date)}`;
                          })()}
                          {l.half_day ? " (Half Day)" : ""}
                        </Typography>
                      </Box>
                      <Chip
                        label={l.leave_type}
                        size="small"
                        sx={{
                          bgcolor: color + "15",
                          color,
                          fontWeight: 700,
                          fontSize: "0.6rem",
                          textTransform: "capitalize",
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Event Cards — admin only */}
      {user?.role === "admin" && (
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <EventCard
            title="Upcoming Birthdays"
            icon={<CakeIcon />}
            iconColor="#FF6B35"
            gradient="linear-gradient(135deg, #E64A19 0%, #FF7043 100%)"
            borderColor="#FF6B35"
            items={upcomingBirthdays}
            emptyMsg="No birthdays this month"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <EventCard
            title="Work Anniversaries"
            icon={<WorkIcon />}
            iconColor="#1565C0"
            gradient="linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)"
            borderColor="#1565C0"
            items={workAnniversaries}
            emptyMsg="No work anniversaries this month"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <EventCard
            title="Wedding Anniversaries"
            icon={<FavoriteIcon />}
            iconColor="#C62828"
            gradient="linear-gradient(135deg, #C62828 0%, #EF5350 100%)"
            borderColor="#C62828"
            items={weddingAnniversaries}
            emptyMsg="No wedding anniversaries this month"
          />
        </Grid>
      </Grid>
      )}

      {/* Recent Projects */}
      <Card className="mb-50" sx={{ boxShadow: "0 4px 20px rgba(30,58,95,0.08)" }}>
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2.5,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {isTeamMember ? "My Assigned Projects" : "Recent Projects"}
            </Typography>
            <Chip
              label={recentProjects.length + " projects"}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Box>
          <Stack spacing={1.5}>
            {recentProjects.map((p) => (
              <Box
                key={p.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "background.default",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": {
                    bgcolor: STATUS_COLORS[p.status] + "08",
                    borderColor: STATUS_COLORS[p.status] + "40",
                  },
                  transition: "all 0.15s",
                }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: STATUS_COLORS[p.status] + "20",
                    color: STATUS_COLORS[p.status],
                    fontSize: "0.9rem",
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {p.name.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    by {p.creator?.name}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ width: 100 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.3,
                      }}
                    ></Box>
                  </Box>
                  <Chip
                    label={p.status.replace("_", " ")}
                    size="small"
                    sx={{
                      bgcolor: STATUS_COLORS[p.status] + "18",
                      color: STATUS_COLORS[p.status],
                      fontWeight: 700,
                      fontSize: "0.65rem",
                      minWidth: 70,
                      textAlign: "center",
                    }}
                  />
                </Box>
              </Box>
            ))}
            {recentProjects.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <FolderIcon
                  sx={{
                    fontSize: 40,
                    opacity: 0.15,
                    display: "block",
                    mx: "auto",
                    mb: 1,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {isTeamMember
                    ? "You are not assigned to any projects yet."
                    : "No projects yet."}
                </Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}